'use client'

import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type { CellClickedEvent, CellMouseOverEvent } from 'ag-grid-community'
import type { ProposalDetailsQueryParams } from '@/app/(dashboard)/proposals/[id]/page'
import type { AccountDetail } from '@/types/api/accounts'
import type { Mode } from '@/types/enums/mode'

import { Text, TextColor, TextSize } from '@moment/design-system-ui/text'
import debounce from 'lodash/debounce'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import { type RootState, useAppDispatch } from '@/app/store'
import Table from '@/components/atomic/table'
import { TableDataModel, TableSize } from '@/components/atomic/table/types'
import { getTimeRangeParams } from '@/components/molecules/price-chart/helpers'
import { TimeRange } from '@/components/molecules/price-chart/types'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { useRatingColumns } from '@/hooks/use-rating-columns'
import useUrlQueryParams from '@/hooks/use-url-query-params'
import { getInstrument, getMarks, getPrices } from '@/legacy-api/data-slice'
import { useSecurityItemsByIsin } from '@/portfolio/proposal-builder/hooks/use-order-security-instruments'
import { getIdentifierPreference } from '@/shared/helpers/identifier-preference'
import { AccountDetailsHoldingsTableColumnId } from '@/types/enums/accounts'
import { PaneType } from '@/types/enums/pane-type'
import { handleTableCellClick } from '@/utils/table-cell-click'

import holdingsColumnDefinitions from './columns'
import { convertToPositions } from './helpers'

interface ProposalAccountHoldingsTableProps {
  accountDetails: AccountDetail | undefined
  previousAccountDetails: AccountDetail | undefined
  proposalOrders: ProposalOrder[]
  isLoading: boolean
  mode?: Mode
  onTogglePane: (paneType: PaneType | null, orderIdOrIsin?: string) => void
  onOrderRemoveAction?: (order: ProposalOrder) => void
  onOrderSwapAction?: (order: ProposalOrder) => void
  onOrderEditAction?: (order: ProposalOrder) => void
  rowNodeLoading?: Record<string, boolean>
}

// Default ag-Grid skeleton loading cell renderer
const defaultLoadingCellRenderer = () => {
  return (
    <div className="w-full h-full flex items-center">
      <div className="w-full h-5 bg-moment-blue-grey-200 dark:bg-moment-blue-grey-900 rounded animate-pulse" />
    </div>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ProposalAccountHoldingsTable({
  accountDetails,
  previousAccountDetails,
  proposalOrders,
  isLoading,
  mode,
  onTogglePane,
  onOrderRemoveAction,
  onOrderSwapAction,
  onOrderEditAction,
  rowNodeLoading = {},
}: ProposalAccountHoldingsTableProps) {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const searchTerm = useTieredSelector(
    (state: RootState) => state.proposals.searchTerm,
    (params: ProposalDetailsQueryParams) => params.searchTerm,
  )

  const selectedIsin = useTieredSelector(
    (state: RootState) => state.proposals.isin,
    (params: ProposalDetailsQueryParams) => params.isin,
  )

  const selectedSleeve = useTieredSelector(
    (state: RootState) => state.proposals.selectedSleeve,
    (params: ProposalDetailsQueryParams) => params.selectedSleeve,
  )

  const selectedOrder = useTieredSelector(
    (state: RootState) => state.proposals.selectedOrder,
    (params: ProposalDetailsQueryParams) => params.selectedOrder,
  )

  const { setQueryParams } = useUrlQueryParams<ProposalDetailsQueryParams>()

  const handleCellClicked = useCallback(
    (event: CellClickedEvent) => {
      // Don't process clicks for rows that are in loading state
      if (rowNodeLoading[event.data.isin]) {
        return
      }

      // Skip row selection when clicking the action button column
      if (
        event.column.getColId() ===
        AccountDetailsHoldingsTableColumnId.REMOVE_ORDER
      ) {
        return
      }

      // Get the corresponding order ID if this holding has one
      const correspondingOrder = event.data.corresponding_order
      const orderId = correspondingOrder?.moment_order_id

      if (correspondingOrder && orderId) {
        // Handle order-based selection like the orders table
        if (orderId === selectedOrder) {
          event.node.setSelected(false)
          onTogglePane(null)
        } else {
          onTogglePane(PaneType.ProposalOrderDetails, orderId)
        }
      } else {
        // For pure holdings (no corresponding order), use bond preview behavior
        const noIsinSelected = !selectedIsin
        const isinMatches = selectedIsin === event.data.isin

        if (noIsinSelected || !isinMatches) {
          event.node.setSelected(true)
        } else if (isinMatches) {
          event.node.setSelected(false)
        }

        handleTableCellClick<ProposalDetailsQueryParams>(
          event,
          selectedIsin,
          setQueryParams,
          'isin',
          router,
        )

        if (noIsinSelected || isinMatches) {
          // Pass the ISIN as the orderId parameter for BondPreview panes
          onTogglePane(PaneType.BondPreview, event.data.isin)
        }
      }
    },
    [
      rowNodeLoading,
      selectedOrder,
      selectedIsin,
      setQueryParams,
      router,
      onTogglePane,
    ],
  )

  const debouncedPreloadData = useMemo(
    () =>
      debounce((isin: string) => {
        dispatch(getInstrument.initiate(isin))
        dispatch(getMarks.initiate({ instruments: [isin] }))
        const { start, end, granularity } = getTimeRangeParams(
          TimeRange.ONE_MONTH,
        )
        dispatch(
          getPrices.initiate({
            instrumentID: isin,
            frequency: granularity,
            start,
            end,
          }),
        )
      }, 200),
    [dispatch],
  )

  const handleCellHover = useCallback(
    (event: CellMouseOverEvent) => {
      const isin = event.data.isin
      if (isin) {
        debouncedPreloadData(isin)
      }
    },
    [debouncedPreloadData],
  )

  // Get identifier preference from centralized hook
  const identifierPreference = getIdentifierPreference()

  const columnDefs = useMemo(() => {
    const columnIdToDef = holdingsColumnDefinitions(identifierPreference)

    // Set the context for the remove order column if handlers are provided
    const removeOrderColumn = columnIdToDef.get(
      AccountDetailsHoldingsTableColumnId.REMOVE_ORDER,
    )
    if (
      removeOrderColumn &&
      (onOrderRemoveAction || onOrderSwapAction || onOrderEditAction)
    ) {
      removeOrderColumn.context = {
        handleOrderRemoveAction: onOrderRemoveAction,
        handleOrderSwapAction: onOrderSwapAction,
        handleOrderEditAction: onOrderEditAction,
      }
    }

    // Add loading cell selector to all columns
    Array.from(columnIdToDef.values()).forEach((column) => {
      // Preserve original cell renderer
      const originalCellRenderer = column.cellRenderer

      // Use cell renderer selector to dynamically choose renderer
      column.cellRendererSelector = (params) => {
        // Check if the row is in loading state by its ISIN
        const isin = params.data?.isin

        if (isin && rowNodeLoading[isin]) {
          // Use loading cell renderer for this cell
          return {
            component: defaultLoadingCellRenderer,
          }
        }

        // Use the original cell renderer for normal cells
        if (originalCellRenderer) {
          return {
            component: originalCellRenderer,
          }
        }

        return undefined
      }

      // Remove the original cell renderer since we're using selector
      column.cellRenderer = undefined
    })

    return [...columnIdToDef.values()]
  }, [
    identifierPreference,
    onOrderRemoveAction,
    onOrderSwapAction,
    onOrderEditAction,
    rowNodeLoading,
  ])

  // Apply rating column header updates
  const finalColumnDefs = useRatingColumns(columnDefs)

  // At some point, we will have to flatten the proposal order to the allocation level
  const accountOrders = useMemo(() => {
    return proposalOrders.filter((order) =>
      order.allocations.some(
        (allocation) =>
          allocation.moment_account_id === accountDetails?.account_id,
      ),
    )
  }, [proposalOrders, accountDetails])

  const positionInputs = useMemo(() => {
    if (!accountDetails || !previousAccountDetails) {
      return null
    }

    const filteredLots = accountDetails.lots.filter(
      // If no sleeve is selected, show all holdings
      // Otherwise, show holdings for the selected sleeve
      (lot) => !selectedSleeve || lot.sleeve_id === selectedSleeve,
    )
    const filteredPreviousLots = previousAccountDetails.lots.filter(
      (lot) => !selectedSleeve || lot.sleeve_id === selectedSleeve,
    )

    const filteredOrders = accountOrders.filter(
      (order) =>
        !selectedSleeve ||
        order.allocations.some(
          (allocation) => allocation.sleeve_id === selectedSleeve,
        ),
    )

    return {
      lots: filteredLots,
      previousLots: filteredPreviousLots,
      orders: filteredOrders,
      accountId: accountDetails.account_id,
    }
  }, [accountDetails, previousAccountDetails, accountOrders, selectedSleeve])

  const isins = useMemo(
    () =>
      positionInputs
        ? [
            ...positionInputs.lots.map((lot) => lot.instrument.isin),
            ...positionInputs.orders.map((order) => order.instrument_id),
          ]
        : [],
    [positionInputs],
  )
  const security = useSecurityItemsByIsin(isins, 'holdings')

  const positions = useMemo(() => {
    if (
      !positionInputs ||
      security.status === 'loading' ||
      security.status === 'failed'
    ) {
      return []
    }
    return convertToPositions(
      positionInputs.lots,
      positionInputs.previousLots,
      positionInputs.orders,
      positionInputs.accountId,
      selectedSleeve,
      security.status === 'ready' ? security.items : undefined,
    )
  }, [positionInputs, security, selectedSleeve])

  const gridOptions = useMemo(() => {
    return {
      // Suppress the central loading overlay text while keeping row-level skeleton loading
      suppressOverlays: ['loading' as const],
      // Ensure rows are properly identified by ISIN
      // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
      getRowId: (params: any) => params.data.isin,
    }
  }, [])

  if (security.status === 'failed') {
    return (
      <Text size={TextSize.Small} color={TextColor.Secondary}>
        Bond reference data failed to load
      </Text>
    )
  }

  return (
    <Table
      mode={mode}
      tableSize={TableSize.Full}
      customRowHeight={50}
      gridModel={TableDataModel.ClientSide}
      columnDefs={finalColumnDefs}
      checkboxSelection={false}
      data={positions}
      onCellClicked={handleCellClicked}
      loading={isLoading || security.status === 'loading'}
      searchTerm={searchTerm || ''}
      onCellHover={handleCellHover}
      gridOptions={gridOptions}
      idConfig={{
        clientSideField: 'id',
        serverSideField: 'id',
      }}
    />
  )
}
